import type { ApiMeta, StandardApiResponse } from '@/types/api';
import { getAccessToken, getRefreshToken, setAccessToken, setRefreshToken, clearTokens } from '@/lib/auth';
import { SESSION_EVENTS } from '@/hooks/useTokenManager';
import { tabSync } from '@/lib/tabSync';

// =============================================================================
// PROJ-035: Session Expiration Handling - 401 Interceptor with Request Queue
// PROJ-035 Enhancement: Multi-Tab Synchronization with BroadcastChannel
// =============================================================================

/**
 * Request queue entry for 401 handling
 */
type QueuedRequest = {
  resolve: (response: Response) => void;
  reject: (error: Error) => void;
  url: string;
  options: RequestInit;
};

/** Flag to prevent multiple simultaneous refresh attempts */
let isRefreshing = false;

/** Queue of requests waiting for token refresh */
let requestQueue: QueuedRequest[] = [];

/** Track if another tab is refreshing */
let otherTabRefreshing = false;

/** Promise to wait for other tab's refresh completion */
let waitingForOtherTab: Promise<string | null> | null = null;

/**
 * Process all queued requests after token refresh attempt
 */
async function processQueue(newToken: string | null): Promise<void> {
  const queue = [...requestQueue];
  requestQueue = [];

  for (const request of queue) {
    if (newToken) {
      // Retry with new token
      try {
        const response = await fetch(request.url, {
          ...request.options,
          headers: {
            ...(request.options.headers || {}),
            Authorization: `Bearer ${newToken}`,
          },
        });
        request.resolve(response);
      } catch (error) {
        request.reject(error as Error);
      }
    } else {
      // Refresh failed, reject all queued requests
      request.reject(new Error('Session expired - refresh failed'));
    }
  }
}

// =============================================================================
// Tab Synchronization Event Handlers
// =============================================================================

/**
 * Initialize tab sync listeners
 * Should be called once when the app initializes
 */
function initTabSync() {
  // Listen for refresh started from other tabs
  tabSync.on('REFRESH_STARTED', (message) => {
    if (message.type === 'REFRESH_STARTED') {
      otherTabRefreshing = true;

      // Create a promise to wait for the refresh to complete
      waitingForOtherTab = new Promise((resolve) => {
        const onComplete = (msg: any) => {
          if (msg.type === 'REFRESH_COMPLETED' && msg.tabId === message.tabId) {
            otherTabRefreshing = false;
            waitingForOtherTab = null;
            tabSync.off('REFRESH_COMPLETED', onComplete);

            if (msg.success) {
              // The other tab will broadcast TOKEN_REFRESHED, so we'll get the token there
              const token = getAccessToken();
              resolve(token);
            } else {
              resolve(null);
            }
          }
        };

        tabSync.on('REFRESH_COMPLETED', onComplete);

        // Timeout after 30 seconds
        setTimeout(() => {
          otherTabRefreshing = false;
          waitingForOtherTab = null;
          tabSync.off('REFRESH_COMPLETED', onComplete);
          resolve(null);
        }, 30000);
      });
    }
  });

  // Listen for token refreshed from other tabs
  tabSync.on('TOKEN_REFRESHED', (message) => {
    if (message.type === 'TOKEN_REFRESHED') {
      setAccessToken(message.accessToken);
      if (message.refreshToken) {
        setRefreshToken(message.refreshToken);
      }
      // Emit refreshed event for this tab's listeners
      window.dispatchEvent(new CustomEvent(SESSION_EVENTS.REFRESHED));
    }
  });

  // Listen for session expired from other tabs
  tabSync.on('SESSION_EXPIRED', (message) => {
    if (message.type === 'SESSION_EXPIRED') {
      clearTokens();
      window.dispatchEvent(new CustomEvent(SESSION_EVENTS.EXPIRED));
    }
  });

  // Listen for logout from other tabs
  tabSync.on('USER_LOGGED_OUT', (message) => {
    if (message.type === 'USER_LOGGED_OUT') {
      clearTokens();
      window.dispatchEvent(new CustomEvent(SESSION_EVENTS.EXPIRED));
      // Redirect to login if needed
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
  });

}

// Initialize tab sync when module loads
if (typeof window !== 'undefined') {
  initTabSync();
}

/**
 * Attempt to refresh the access token using the refresh token
 * Enhanced with tab synchronization to prevent race conditions
 */
async function attemptTokenRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  // Broadcast that this tab is starting a refresh
  tabSync.broadcast({
    type: 'REFRESH_STARTED',
    tabId: tabSync.getTabId(),
    timestamp: Date.now()
  });

  try {
    const response = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.ok) {
      const result = await response.json();
      const data = result.data || result;

      if (data.accessToken) {
        setAccessToken(data.accessToken);

        // If new refresh token provided (rotation), update it
        if (data.refreshToken) {
          setRefreshToken(data.refreshToken);
        }

        // Broadcast successful refresh to other tabs
        tabSync.broadcast({
          type: 'TOKEN_REFRESHED',
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          timestamp: Date.now()
        });

        // Broadcast completion status
        tabSync.broadcast({
          type: 'REFRESH_COMPLETED',
          tabId: tabSync.getTabId(),
          success: true,
          timestamp: Date.now()
        });

        window.dispatchEvent(new CustomEvent(SESSION_EVENTS.REFRESHED));
        return data.accessToken;
      }
    }

    // Broadcast failure to other tabs
    tabSync.broadcast({
      type: 'REFRESH_COMPLETED',
      tabId: tabSync.getTabId(),
      success: false,
      timestamp: Date.now()
    });

    tabSync.broadcast({
      type: 'SESSION_EXPIRED',
      timestamp: Date.now()
    });

    return null;
  } catch (error) {
    console.error('[401Interceptor] Token refresh error:', error);

    // Broadcast error to other tabs
    tabSync.broadcast({
      type: 'REFRESH_COMPLETED',
      tabId: tabSync.getTabId(),
      success: false,
      timestamp: Date.now()
    });

    return null;
  }
}

/**
 * Handle 401 response: queue request, attempt refresh, retry or fail
 * Enhanced with tab synchronization to prevent race conditions
 */
async function handle401Response(
  url: string,
  options: RequestInit
): Promise<Response> {
  // If another tab is refreshing, wait for it
  if (otherTabRefreshing && waitingForOtherTab) {
    const newToken = await waitingForOtherTab;

    if (newToken) {
      // Other tab succeeded - retry with new token
      return fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          Authorization: `Bearer ${newToken}`,
        },
      });
    } else {
      // Other tab failed - session expired
      throw new Error('SESSION_EXPIRED');
    }
  }

  // If this tab is already refreshing, queue this request
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      requestQueue.push({ resolve, reject, url, options });
    });
  }

  isRefreshing = true;

  try {
    const newToken = await attemptTokenRefresh();

    if (newToken) {
      // Refresh successful - retry original request and process queue
      // Retry the original request
      const retryResponse = await fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          Authorization: `Bearer ${newToken}`,
        },
      });

      // Process queued requests
      await processQueue(newToken);

      return retryResponse;
    } else {
      // Refresh failed - emit expired event and clear tokens
      clearTokens();
      window.dispatchEvent(new CustomEvent(SESSION_EVENTS.EXPIRED));

      // Reject all queued requests
      await processQueue(null);

      // Return a synthetic 401 response to trigger error handling
      throw new Error('SESSION_EXPIRED');
    }
  } finally {
    isRefreshing = false;
  }
}

// =============================================================================
// API Error Class
// =============================================================================

export class ApiError extends Error {
  readonly code: string;
  readonly status?: number;
  readonly details?: unknown;
  readonly requestId?: string | null;
  readonly meta?: ApiMeta;

  constructor(message: string, options: {
    code?: string;
    status?: number;
    details?: unknown;
    requestId?: string | null;
    meta?: ApiMeta;
  } = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = options.code ?? 'API_ERROR';
    this.status = options.status;
    this.details = options.details;
    this.requestId = options.requestId;
    this.meta = options.meta;
  }
}

export type ParsedApiResponse<T> = {
  data: T;
  meta: ApiMeta;
  raw: StandardApiResponse<T>;
};

/**
 * Type guard utility to extract data from ParsedApiResponse.
 *
 * CRITICAL: ALL API client functions MUST use this pattern to extract data:
 *
 * ✅ CORRECT:
 * ```typescript
 * const response = await apiRequest<T>(...);
 * return response.data;  // or: return extractData(response);
 * ```
 *
 * ❌ INCORRECT:
 * ```typescript
 * return apiRequest<T>(...);  // Returns wrapper, not data
 * ```
 *
 * @param response - The ParsedApiResponse wrapper from apiRequest()
 * @returns The unwrapped data of type T
 */
export function extractData<T>(response: ParsedApiResponse<T>): T {
  return response.data;
}

const DEFAULT_META: ApiMeta = {
  timestamp: new Date(0).toISOString(),
  version: '1.0',
  requestId: null
};

function buildFallbackMeta(response: Response): ApiMeta {
  return {
    ...DEFAULT_META,
    timestamp: new Date().toISOString(),
    requestId: response.headers.get('x-request-id')
  };
}

export async function parseStandardApiResponse<T>(
  response: Response,
  context?: string
): Promise<ParsedApiResponse<T>> {
  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  const body = isJson
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  const fallbackMeta = buildFallbackMeta(response);

  // If server returned the new standard format (with meta)
  if (body && typeof body === 'object' && 'success' in body && 'data' in body && 'meta' in body) {
    const std = body as StandardApiResponse<T>;

    if (!std.success) {
      console.error('[parseStandardApiResponse] API returned success=false:', {
        error: std.error,
        status: response.status,
        url: response.url,
        meta: std.meta
      });
      const message =
        std.error?.message ??
        context ??
        `Solicitud rechazada${std.error?.code ? ` (${std.error.code})` : ''}`;
      throw new ApiError(message, {
        code: std.error?.code ?? 'API_ERROR',
        status: response.status,
        details: std.error?.details,
        requestId: std.meta?.requestId,
        meta: std.meta ?? fallbackMeta
      });
    }

    if (!response.ok) {
      const message =
        std.error?.message ??
        context ??
        `Error HTTP ${response.status}`;
      throw new ApiError(message, {
        code: std.error?.code ?? 'HTTP_ERROR',
        status: response.status,
        details: std.error?.details,
        requestId: std.meta?.requestId,
        meta: std.meta ?? fallbackMeta
      });
    }

    return {
      data: std.data as T,
      meta: std.meta ?? fallbackMeta,
      raw: std
    };
  }

  // If server returned error format without meta (legacy error middleware)
  if (body && typeof body === 'object' && 'success' in body && body.success === false && 'error' in body) {
    const errorObj = (body as any).error;
    const message = errorObj?.message ?? context ?? `Error HTTP ${response.status}`;
    throw new ApiError(message, {
      code: errorObj?.code ?? 'API_ERROR',
      status: errorObj?.status ?? response.status,
      details: errorObj?.details,
      requestId: fallbackMeta.requestId,
      meta: fallbackMeta
    });
  }

  // Legacy or unexpected payloads
  if (!response.ok) {
    console.error('[parseStandardApiResponse] HTTP error (legacy handler):', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      context,
      bodyType: typeof body,
      bodyKeys: body && typeof body === 'object' ? Object.keys(body) : null,
      body: body
    });
    const legacyMessage =
      (body && typeof body === 'object' && 'error' in body && typeof (body as any).error === 'string'
        ? (body as any).error
        : typeof body === 'string' && body.trim().length
          ? body
          : undefined) ?? context ?? `HTTP ${response.status}`;

    throw new ApiError(legacyMessage, {
      status: response.status,
      code: 'HTTP_ERROR',
      details: body,
      requestId: fallbackMeta.requestId,
      meta: fallbackMeta
    });
  }

  const successPayload = ((): ParsedApiResponse<T> => {
    // Handle { success, data, pagination } format (legacy)
    if (body && typeof body === 'object' && 'data' in body && 'pagination' in body) {
      const legacyMeta: ApiMeta = {
        ...fallbackMeta,
        pagination: (body as any).pagination
      };

      return {
        data: (body as any).data as T,
        meta: legacyMeta,
        raw: {
          success: true,
          data: (body as any).data as T,
          error: null,
          meta: legacyMeta
        }
      };
    }

    // Handle { success, data } format (without meta or pagination)
    if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
      return {
        data: (body as any).data as T,
        meta: fallbackMeta,
        raw: {
          success: true,
          data: (body as any).data as T,
          error: null,
          meta: fallbackMeta
        }
      };
    }

    // Fallback: treat entire body as data
    return {
      data: body as T,
      meta: fallbackMeta,
      raw: {
        success: true,
        data: body as T,
        error: null,
        meta: fallbackMeta
      }
    };
  })();

  return successPayload;
}

/**
 * PROJ-004: Standard Fetch with No-Cache Headers
 * PROJ-035: Enhanced with 401 Interceptor for Session Expiration
 *
 * Wraps fetch() to:
 * 1. Add no-cache headers to all requests
 * 2. Auto-inject Authorization header if token exists
 * 3. Intercept 401 responses and attempt token refresh
 * 4. Queue concurrent requests during refresh
 *
 * Also rewrites relative /api URLs to use absolute VITE_API_URL in production.
 *
 * @param url - Request URL
 * @param options - Fetch options
 * @param skipAuthRetry - Skip 401 handling (for auth endpoints)
 * @returns Promise<Response>
 */
export async function fetchWithNoCache(
  url: string,
  options: RequestInit = {},
  skipAuthRetry = false
): Promise<Response> {
  const noCacheHeaders = {
    'Cache-Control': 'no-store, no-cache',
    'Pragma': 'no-cache'
  };

  // Rewrite relative /api calls to absolute gateway base when VITE_API_URL is provided
  let finalUrl = url;
  const API_BASE = import.meta.env.VITE_API_URL?.trim?.();
  if (typeof API_BASE === 'string' && API_BASE && url.startsWith('/api')) {
    const base = API_BASE.replace(/\/$/, '');
    const rest = url.replace(/^\/api\/v1/, '');
    finalUrl = base + rest;
  }

  // Auto-detect Content-Type for JSON bodies
  const headers: Record<string, string> = {
    ...noCacheHeaders
  };

  // Merge existing headers
  if (options.headers) {
    const existingHeaders = new Headers(options.headers);
    existingHeaders.forEach((value, key) => {
      headers[key] = value;
    });
  }

  // PROJ-035: Auto-inject Authorization header if token exists and not already set
  if (!headers['Authorization'] && !headers['authorization']) {
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // If body is a string (likely JSON) and Content-Type is not set, add it
  if (options.body && typeof options.body === 'string' && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  const finalOptions: RequestInit = {
    ...options,
    headers,
    cache: 'no-store' // Request cache mode
  };

  const response = await fetch(finalUrl, finalOptions);

  // PROJ-035: Handle 401 responses with token refresh
  // Skip for auth endpoints (login, refresh) to avoid infinite loops
  const isAuthEndpoint = finalUrl.includes('/auth/login') ||
                          finalUrl.includes('/auth/refresh') ||
                          finalUrl.includes('/auth/register');

  if (response.status === 401 && !skipAuthRetry && !isAuthEndpoint) {
    return handle401Response(finalUrl, finalOptions);
  }

  return response;
}

/**
 * PROJ-004: Standard API Request with No-Cache
 *
 * Convenience wrapper that combines fetchWithNoCache and parseStandardApiResponse.
 *
 * @param url - Request URL
 * @param options - Fetch options
 * @returns Promise<ParsedApiResponse<T>>
 */
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<ParsedApiResponse<T>> {
  const response = await fetchWithNoCache(url, options);
  return parseStandardApiResponse<T>(response, url);
}

